import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Loader2, MessageSquare, Play } from 'lucide-react'
import { Panel, Group, Separator, type GroupImperativeHandle } from 'react-resizable-panels'
import { WorkflowChat, WorkflowDocument, WelcomeChat, SKILL_STARTERS } from '@/components/workflow'
import { useAuth } from '@/contexts/auth-context'
import { useWorkflowChat } from '@/hooks/useWorkflowChat'
import type { ChatAttachment } from '@/hooks/useWorkflowChat'
import { useUiLayout } from '@/hooks/useUiLayout'
import { useLocalToolBridge } from '@/hooks/useLocalToolBridge'
import { useDesktopNotifications } from '@/hooks/useDesktopNotifications'
import { useProjectStoreSync, useUndoRedoShortcuts } from '@/hooks/useProjectStore'
import { useProjectStore } from '@/lib/projectStore'
import { VideoSeekProvider } from '@/contexts/video-seek-context'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { cn } from '@/lib/utils'

// Check if a URL param looks like a Convex ID (long) vs shortcode (6 chars)
function isConvexId(param: string): boolean {
  return param.length > 20
}

export default function WorkflowEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const nuxData = (location.state as any)?.nuxData as {
    direction?: string | null
    clusters?: { id: string; label: string; file_count: number; date_range?: string }[]
    totalFiles?: number
    globalContext?: string
  } | undefined
  const getSubscriptionStatus = useAction(api.stripe.getSubscriptionStatus)

  // Resolve shortcode → workflow ID
  const isShortcode = id && id !== 'new' && !isConvexId(id)
  const shortcodeWorkflow = useQuery(
    api.workflows.getByShortcode,
    isShortcode ? { shortcode: id } : 'skip'
  )

  // Mobile tab state
  const [mobileTab, setMobileTab] = useState<'chat' | 'preview'>('chat')

  // Progressive disclosure: hide document panel until content exists
  const [showDocumentPanel, setShowDocumentPanel] = useState(false)

  // Subscription info from DB (synced via webhooks)
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    plan: string
    creditLimit: number
  } | null>(null)

  // Fetch subscription status
  useEffect(() => {
    if (!user) return
    getSubscriptionStatus()
      .then(setSubscriptionInfo)
      .catch(() => setSubscriptionInfo({ plan: 'free', creditLimit: 0 }))
  }, [user])

  // Handle return from Stripe upgrade
  const [hasCheckedUpgrade, setHasCheckedUpgrade] = useState(false)
  useEffect(() => {
    const upgraded = searchParams.get('upgraded')
    if (upgraded === 'success' && !hasCheckedUpgrade && id && id !== 'new') {
      setHasCheckedUpgrade(true)

      // Clean up URL immediately
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('upgraded')
      setSearchParams(newParams, { replace: true })

      // Refresh subscription status
      getSubscriptionStatus()
        .then(setSubscriptionInfo)
        .catch((error) => {
          console.error('Failed to get subscription status:', error)
        })
    }
  }, [searchParams, hasCheckedUpgrade, setSearchParams, id])

  // Calculate credits
  const creditLimit = subscriptionInfo?.creditLimit ?? 0
  const userCredits = user
    ? Math.max(0, creditLimit - (user.creditsUsedThisPeriod ?? 0))
    : 0

  // Only send credits to backend when subscriptionInfo is loaded
  // This prevents sending wrong credits (182) while subscription is still loading
  const userCreditsForBackend = subscriptionInfo ? userCredits : undefined

  // Workflow creation — deferred until first message for new workflows
  const createWorkflow = useMutation(api.workflows.create)
  const resolvedId = isShortcode
    ? (shortcodeWorkflow?._id ?? null)
    : id === 'new'
      ? null
      : (id as Id<'workflows'>)
  const [workflowId, setWorkflowId] = useState<Id<'workflows'> | null>(
    id === 'new' ? null : (!isShortcode ? (id as Id<'workflows'>) : null)
  )
  const creationInitiated = useRef(false)
  const pendingMessageRef = useRef<{ msg: string; opts?: { attachments?: ChatAttachment[]; skill?: string } } | null>(null)

  // When shortcode resolves, set the workflowId
  useEffect(() => {
    if (resolvedId && !workflowId) {
      setWorkflowId(resolvedId)
    }
  }, [resolvedId])

  // Fetch workflow data — skip if shortcode query already has it
  const workflowById = useQuery(
    api.workflows.get,
    workflowId && !isShortcode ? { id: workflowId } : 'skip'
  )
  const workflow = isShortcode ? shortcodeWorkflow : workflowById
  // Store is the sole source of truth for clips.
  // Convex subscriptions hydrate the store; UI always reads from store.
  const storeClipOrder = useProjectStore((s) => s.clipOrder)
  const storeClips = useProjectStore((s) => s.clips)
  const storeHydrated = useProjectStore((s) => s._hydrated)

  const clips = useMemo(() => {
    if (!storeHydrated) return undefined
    return storeClipOrder.map((id) => storeClips[id]).filter(Boolean) as any[]
  }, [storeHydrated, storeClipOrder, storeClips])

  // Mutations
  const updateNarrative = useMutation(api.workflows.updateNarrative)

  // Use streaming chat hook
  const {
    messages: chatMessages,
    isStreaming: isChatStreaming,
    isUploading: isChatUploading,
    uploadStatus: chatUploadStatus,
    error: chatError,
    sendMessage,
    cancelStream,
    hasLoaded: chatHasLoaded,
    setPendingMessage,
  } = useWorkflowChat({
    workflowId,
    userCredits: userCreditsForBackend, // Only pass after subscriptionInfo loads
  })

  // Desktop tool bridge: auto-executes local tools when in Electron
  useLocalToolBridge(workflowId)
  useDesktopNotifications(clips, workflow?.title)

  // Project store: local-first state with undo/redo
  useProjectStoreSync(workflowId ?? undefined)
  useUndoRedoShortcuts()

  // Welcome state: show for genuinely new workflows or loaded workflows with no messages
  // Never show welcome state when navigating to an existing project (shortcode or ID)
  const isNewProject = id === 'new'
  const autoStartRef = useRef(!!(location.state as any)?.autoStart)
  const isWelcomeState = !autoStartRef.current && ((isNewProject && !workflowId) || (!isNewProject && workflowId && chatHasLoaded && chatMessages.length === 0 && !isChatStreaming))

  // Create workflow on demand, then send the queued message
  const handleWelcomeSend = useCallback(async (msg: string, opts?: { attachments?: ChatAttachment[]; skill?: string }) => {
    if (workflowId) {
      sendMessage(msg, { attachments: opts?.attachments })
      return
    }
    if (creationInitiated.current) return
    creationInitiated.current = true

    // Set optimistic message immediately so the editor never shows empty
    setPendingMessage(msg, opts?.attachments)

    pendingMessageRef.current = { msg, opts }
    try {
      const createArgs: Parameters<typeof createWorkflow>[0] = {}

      if (opts?.skill) {
        createArgs.globalContext = `Skill: ${opts.skill}`
      }

      if (nuxData) {
        const dirLabel = nuxData.direction === 'highlight-reel' ? 'Highlight Reel'
          : nuxData.direction === 'story-video' ? 'Story Video'
          : nuxData.direction === 'social-clip' ? 'Social Clip'
          : undefined

        if (dirLabel) createArgs.title = dirLabel

        if (nuxData.direction === 'social-clip') {
          createArgs.settings = { aspectRatio: '9:16', defaultDuration: 15 }
        }

        const clusterSummary = (nuxData.clusters ?? [])
          .map(c => `- ${c.label} (${c.file_count} files${c.date_range ? `, ${c.date_range}` : ''})`)
          .join('\n')
        if (clusterSummary) {
          createArgs.globalContext = `User selected ${nuxData.clusters?.length ?? 0} media groups during onboarding (${nuxData.totalFiles ?? 0} total files):\n${clusterSummary}\nDirection: ${dirLabel ?? 'custom'}`
        }

        if (nuxData.globalContext && !createArgs.globalContext) {
          createArgs.globalContext = nuxData.globalContext
        }
      }

      const result = await createWorkflow(createArgs)
      setWorkflowId(result.id)
      window.history.replaceState(null, '', `/video/${result.shortcode}`)
    } catch (error) {
      console.error('Failed to create workflow:', error)
      creationInitiated.current = false
      pendingMessageRef.current = null
      navigate('/videos')
    }
  }, [workflowId, sendMessage, setPendingMessage, createWorkflow, navigate, nuxData])

  // Send pending message once the hook is ready with the new workflowId
  useEffect(() => {
    if (workflowId && chatHasLoaded && pendingMessageRef.current) {
      const { msg, opts } = pendingMessageRef.current
      pendingMessageRef.current = null
      // Pass pre-uploaded attachments directly (skips re-upload in sendMessage)
      sendMessage(msg, { attachments: opts?.attachments })
    }
  }, [workflowId, chatHasLoaded, sendMessage])

  // Auto-submit homepage prompt stored in localStorage
  const homepagePromptSent = useRef(false)
  useEffect(() => {
    if (!isNewProject || workflowId || homepagePromptSent.current) return
    const prompt = localStorage.getItem('pendingHomepagePrompt')
    if (prompt) {
      homepagePromptSent.current = true
      localStorage.removeItem('pendingHomepagePrompt')
      handleWelcomeSend(prompt)
    }
  }, [isNewProject, workflowId, handleWelcomeSend])

  const skillParamHandled = useRef(false)
  useEffect(() => {
    if (!isNewProject || workflowId || skillParamHandled.current) return
    const skill = searchParams.get('skill')
    if (skill) {
      skillParamHandled.current = true
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('skill')
      setSearchParams(newParams, { replace: true })
      handleWelcomeSend(SKILL_STARTERS[skill] ?? `Create a ${skill} video.`, { skill })
    }
  }, [isNewProject, workflowId, searchParams, setSearchParams, handleWelcomeSend])

  const PRESET_STARTERS: Record<string, string> = {
    'music-video': 'I want to create a music video.',
    'social-short': 'I want to create a short-form social media video.',
    'podcast': 'I want to edit a podcast into a video.',
    'documentary': 'I want to create a documentary-style video.',
    'commercial': 'I want to create a commercial.',
  }

  const presetParamHandled = useRef(false)
  useEffect(() => {
    if (!isNewProject || workflowId || presetParamHandled.current) return
    const preset = searchParams.get('preset')
    if (preset) {
      presetParamHandled.current = true
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('preset')
      setSearchParams(newParams, { replace: true })
      handleWelcomeSend(PRESET_STARTERS[preset] ?? `I want to create a ${preset.replace(/-/g, ' ')} video.`)
    }
  }, [isNewProject, workflowId, searchParams, setSearchParams, handleWelcomeSend])

  const autoStartSent = useRef(false)
  useEffect(() => {
    const autoStart = (location.state as any)?.autoStart
    if (!autoStart || autoStartSent.current || !workflowId || !chatHasLoaded) return
    autoStartSent.current = true
    window.history.replaceState({}, '', location.pathname)
    sendMessage("Let's start a project using the selected media.")
  }, [location.state, workflowId, chatHasLoaded, sendMessage, location.pathname])

  const handleNarrativeChange = async (narrative: string) => {
    if (!workflowId) return
    try {
      await updateNarrative({ workflowId, narrative })
    } catch (error) {
      console.error('Failed to update narrative:', error)
    }
  }

  // Progressive disclosure: reveal document panel when content exists
  useEffect(() => {
    const hasAudioTracks = workflow?.audioTracks && workflow.audioTracks.length > 0
    if (workflow?.narrative || workflow?.title || (clips && clips.length > 0) || hasAudioTracks) {
      setShowDocumentPanel(true)
    }
  }, [workflow?.narrative, workflow?.title, clips, workflow?.audioTracks])

  const uiLayout = useUiLayout((workflow as any)?.uiLayout)
  const mainGroupRef = useRef<GroupImperativeHandle>(null)

  useEffect(() => {
    if (!mainGroupRef.current) return
    const chatSize = uiLayout.chatPanelSize
    if (chatSize !== 30) {
      mainGroupRef.current.setLayout({ chat: chatSize, document: 100 - chatSize })
    }
  }, [uiLayout.chatPanelSize])

  // Loading state (only for existing workflows loading their data)
  if (id !== 'new' && !workflow) {
    // Still resolving shortcode
    if (isShortcode && shortcodeWorkflow === undefined) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }
    // Shortcode resolved but no workflow found
    if (isShortcode && shortcodeWorkflow === null) {
      return (
        <div className="h-full w-full flex flex-col items-center justify-center bg-background">
          <p className="text-muted-foreground mb-4">Video not found</p>
          <button
            onClick={() => navigate('/videos')}
            className="text-foreground hover:underline"
          >
            Back to videos
          </button>
        </div>
      )
    }
    // Loading by Convex ID
    if (!isShortcode) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }
  }

  // Not found
  if (id !== 'new' && workflow === null) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center bg-background">
        <p className="text-muted-foreground mb-4">Video not found</p>
        <button
          onClick={() => navigate('/videos')}
          className="text-foreground hover:underline"
        >
          Back to videos
        </button>
      </div>
    )
  }

  // Default workflow state while creating
  const workflowData = workflow || {
    status: 'script',
    title: undefined,
    narrative: '',
    characters: [],
    globalContext: undefined,
  }

  const chatPanel = (
    <div className="h-full flex flex-col bg-background">
      <ErrorBoundary level="section" resetKey={workflowId ?? undefined}>
        <WorkflowChat
          workflowId={workflowId}
          messages={chatMessages}
          isStreaming={isChatStreaming}
          isUploading={isChatUploading}
          uploadStatus={chatUploadStatus}
          error={chatError}
          onSendMessage={sendMessage}
          onCancel={cancelStream}
          clips={clips}
        />
      </ErrorBoundary>
    </div>
  )

  const documentPanel = (
    <div className="h-full flex flex-col bg-secondary overflow-hidden relative">
      {/* Placeholder - fades out when document appears */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center text-muted-foreground transition-all duration-500 ${showDocumentPanel ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}>
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-muted-foreground mb-1">Your video will appear here</p>
        <p className="text-xs text-muted-foreground/70">Start by describing your idea in the chat</p>
      </div>
      {/* Document - fades in when ready */}
      <div className={`h-full transition-all duration-500 ${showDocumentPanel && workflowId ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        {workflowId && (
          <ErrorBoundary level="section" resetKey={workflowId}>
            <WorkflowDocument
              workflowId={workflowId}
              workflow={{
                ...workflowData,
                finalVideoUrl: workflow?.finalVideoUrl,
                finalVideoStatus: workflow?.finalVideoStatus,
                finalVideoError: workflow?.finalVideoError,
                audioTracks: workflow?.audioTracks,
                narratorVoice: workflow?.narratorVoice,
                textOverlays: workflow?.textOverlays,
                projectTtsStatus: workflow?.projectTtsStatus,
                projectTtsTimestamps: workflow?.projectTtsTimestamps as any,
                exportDuration: workflow?.exportDuration,
                skillFeatures: workflow?.skillFeatures,
              }}
              clips={clips || []}
              messages={chatMessages}
              onNarrativeChange={handleNarrativeChange}
              credits={subscriptionInfo?.plan === 'free' ? undefined : userCredits}
              isAdmin={user?.isAdmin}
              aspectRatio={workflow?.settings?.aspectRatio}
              uiLayout={uiLayout}
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  )

  const projectTitle = workflow?.title || 'New Film'

  return (
    <VideoSeekProvider>
    <div className="h-full w-full overflow-hidden relative">
      <Helmet>
        <title>{projectTitle} - Footage</title>
      </Helmet>
      {/* Welcome — visible by default, fades out on first message */}
      <div className={cn("absolute inset-0 z-10 transition-all",
        isWelcomeState ? "opacity-100 duration-200" : "opacity-0 scale-95 pointer-events-none duration-500"
      )}>
        <WelcomeChat onSendMessage={handleWelcomeSend} mediaContext={nuxData?.globalContext} />
      </div>

      {/* Editor — hidden until messages loaded (or optimistic message set), fades in */}
      <div className={cn("h-full transition-all duration-500",
        (!chatHasLoaded && chatMessages.length === 0) || isWelcomeState ? "opacity-0 pointer-events-none" : "opacity-100"
      )}>
        {/* Desktop: split-pane layout */}
        <div className="hidden md:block h-full">
          <Group orientation="horizontal" className="h-full" groupRef={mainGroupRef}>
            <Panel id="chat" defaultSize={uiLayout.chatPanelSize} minSize={20}>
              {chatPanel}
            </Panel>
            <Separator className="w-[3px] bg-border/50 hover:bg-primary/30 cursor-col-resize focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 transition-colors relative group">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-[3px] opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-[3px] h-[3px] rounded-full bg-muted-foreground/30" />
                <div className="w-[3px] h-[3px] rounded-full bg-muted-foreground/30" />
                <div className="w-[3px] h-[3px] rounded-full bg-muted-foreground/30" />
              </div>
            </Separator>
            <Panel id="document" defaultSize={100 - uiLayout.chatPanelSize} minSize={40}>
              {documentPanel}
            </Panel>
          </Group>
        </div>

        {/* Mobile: tabbed layout */}
        <div className="md:hidden h-full flex flex-col">
          {/* Tab bar */}
          <div className="flex border-b border-border bg-background flex-shrink-0">
            <button
              onClick={() => setMobileTab('chat')}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                mobileTab === 'chat'
                  ? "text-foreground border-b-2 border-foreground"
                  : "text-muted-foreground"
              )}
            >
              <MessageSquare className="h-4 w-4" />
              Chat
            </button>
            <button
              onClick={() => setMobileTab('preview')}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                mobileTab === 'preview'
                  ? "text-foreground border-b-2 border-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Play className="h-4 w-4" />
              Preview
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {mobileTab === 'chat' ? chatPanel : documentPanel}
          </div>
        </div>
      </div>
    </div>
    </VideoSeekProvider>
  )
}
